import { useEffect, useMemo, useState } from 'react';
import { Check, ChevronsUpDown, Search, UserPlus } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@marine/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@marine/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@marine/ui/popover';
import { formatPhoneUS } from '@marine/utils/formatters';
import { useCustomers } from '@/modules/crm/hooks/useCustomers';
import type { Customer } from '@/modules/crm/types/crm.types';

interface CustomerSearchComboboxProps {
  value?: string | null;
  onChange: (customerId: string | null) => void;
  onQuickCreate?: () => void;
  className?: string;
  placeholder?: string;
  /** Trigger-label fallback when `value` isn't among the loaded results (e.g. preselected id). */
  initialCustomer?: Customer | null;
}

export function CustomerSearchCombobox({
  value,
  onChange,
  onQuickCreate,
  className,
  placeholder = 'Search customer...',
  initialCustomer,
}: CustomerSearchComboboxProps) {
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  
  useEffect(() => {
    const handle = setTimeout(() => {
      setDebouncedQuery(searchQuery.trim());
    }, 250);

    return () => clearTimeout(handle);
  }, [searchQuery]);

  const customerFilters = useMemo(() => {
    const filters: Record<string, any> = { per_page: 25 };
    if (debouncedQuery) {
      filters.search = debouncedQuery;
    }
    return filters;
  }, [debouncedQuery]);

  const { data, isLoading, isFetching } = useCustomers(customerFilters);
  const customers = data?.items || [];
  const isLoadingCustomers = isLoading || isFetching;

  const selectedCustomer =
    customers?.find((c: Customer) => c.id === value) ??
    (initialCustomer && initialCustomer.id === value ? initialCustomer : undefined);

  // Helper to get display name
  const getDisplayName = (customer: Customer) => {
    const fullName = customer.full_name || `${customer.first_name} ${customer.last_name}`;
    return customer.company_name ? `${fullName} (${customer.company_name})` : fullName;
  };

  return (
    <div className={cn('flex gap-2', className)}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className="flex-1 justify-between"
          >
            {selectedCustomer ? (
              <div className="flex items-center gap-2 truncate">
                <span className="truncate">{getDisplayName(selectedCustomer)}</span>
                {selectedCustomer.email && (
                  <span className="text-xs text-muted-foreground truncate">
                    ({selectedCustomer.email})
                  </span>
                )}
              </div>
            ) : (
              <span className="text-muted-foreground">{placeholder}</span>
            )}
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[400px] p-0" align="start">
          <Command shouldFilter={false}>
            <CommandInput 
              placeholder="Search by name, email, or phone..." 
              value={searchQuery}
              onValueChange={setSearchQuery}
            />
            <CommandList>
              {!isLoadingCustomers && (
                <CommandEmpty>
                  <div className="text-center py-6">
                    <Search className="mx-auto h-8 w-8 text-muted-foreground/50 mb-2" />
                    <p className="text-sm text-muted-foreground">No customers found</p>
                    {onQuickCreate && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="mt-2"
                        onClick={() => {
                          setOpen(false);
                          onQuickCreate();
                        }}
                      >
                        <UserPlus className="mr-2 h-4 w-4" />
                        Create New Customer
                      </Button>
                    )}
                  </div>
                </CommandEmpty>
              )}
              <CommandGroup>
                {isLoadingCustomers ? (
                  <div className="py-6 text-center text-sm">Loading customers...</div>
                ) : (
                  customers?.map((customer: Customer) => (
                    <CommandItem
                      key={customer.id}
                      value={String(customer.id)}
                      onSelect={() => {
                        onChange(customer.id === value ? null : customer.id);
                        setOpen(false);
                      }}
                    >
                      <Check
                        className={cn(
                          'mr-2 h-4 w-4',
                          value === customer.id ? 'opacity-100' : 'opacity-0'
                        )}
                      />
                      <div className="flex-1 min-w-0">
                        <div className="font-medium truncate">{getDisplayName(customer)}</div>
                        <div className="text-xs text-muted-foreground truncate">
                          {customer.email}
                          {(customer.phone || customer.mobile) &&
                            ` • ${formatPhoneUS(customer.phone || customer.mobile)}`}
                        </div>
                      </div>
                    </CommandItem>
                  ))
                )}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      {onQuickCreate && (
        <Button
          type="button"
          variant="outline"
          size="icon"
          onClick={onQuickCreate}
          title="Quick Create Customer"
        >
          <UserPlus className="h-4 w-4" />
        </Button>
      )}
    </div>
  );
}
